if (!customElements.get('phofay-customer-reviews')) {
  customElements.define('phofay-customer-reviews', class extends HTMLElement {
    connectedCallback() {
      this.initialize();
      if (this.events) return;
      this.setupObserver = new MutationObserver(() => { this.initialize(); if (this.events) this.setupObserver.disconnect(); });
      this.setupObserver.observe(this, { childList: true, subtree: true });
    }

    initialize() {
      if (this.events || !this.querySelector('[data-dialog-body]')) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.track = this.querySelector('[data-review-track]');
      this.cards = Array.from(this.querySelectorAll('[data-review-card]'));
      this.previous = this.querySelector('[data-previous]');
      this.next = this.querySelector('[data-next]');
      this.status = this.querySelector('[role="status"]');
      this.dialog = this.querySelector('[data-review-dialog]');
      this.previous.addEventListener('click', () => this.move(-1), options);
      this.next.addEventListener('click', () => this.move(1), options);
      this.track.addEventListener('scroll', () => this.update(), { ...options, passive: true });
      // Touch scrolls natively; mouse and pen drag the track (editor mobile preview, narrow windows).
      this.track.addEventListener('pointerdown', event => {
        if (event.pointerType === 'touch' || event.button !== 0 || event.target.closest('button, a')) return;
        this.drag = { id: event.pointerId, x: event.clientX, left: this.track.scrollLeft, moved: false };
      }, options);
      this.track.addEventListener('pointermove', event => {
        const drag = this.drag;
        if (!drag || drag.id !== event.pointerId) return;
        const delta = event.clientX - drag.x;
        if (!drag.moved) {
          if (Math.abs(delta) < 5) return;
          drag.moved = true;
          this.track.setPointerCapture(event.pointerId);
          this.track.classList.add('is-dragging');
        }
        this.track.scrollLeft = drag.left - delta;
      }, options);
      const endDrag = event => {
        const drag = this.drag;
        if (!drag || drag.id !== event.pointerId) return;
        if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
        this.track.classList.remove('is-dragging');
        if (!drag.moved) this.drag = null;
      };
      this.track.addEventListener('pointerup', endDrag, options);
      this.track.addEventListener('pointercancel', endDrag, options);
      // A drag ends with a click on whatever is under the pointer; swallow it so no review opens.
      this.track.addEventListener('click', event => {
        if (this.drag?.moved) { event.preventDefault(); event.stopPropagation(); }
        this.drag = null;
      }, { ...options, capture: true });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home' || event.key === 'End') this.track.scrollTo({ left: event.key === 'Home' ? 0 : this.track.scrollWidth, behavior: this.behavior() });
        else this.move(event.key === 'ArrowLeft' ? -1 : 1);
      }, options);
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-review-open]');
        if (button) this.open(button.closest('[data-review-card]'));
      }, options);
      this.querySelector('[data-dialog-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
      }, options);
      document.addEventListener('shopify:block:select', event => {
        const card = this.cards.find(card => card.dataset.blockId === event.detail?.blockId);
        if (!card) return;
        const offset = card.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
        this.track.scrollTo({ left: this.track.scrollLeft + offset, behavior: 'instant' });
      }, options);
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.track);
      this.update();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
      this.setupObserver?.disconnect();
      this.resizeObserver?.disconnect();
    }

    behavior() { return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'; }

    move(direction) {
      const step = this.cards.length > 1 ? this.cards[1].offsetLeft - this.cards[0].offsetLeft : this.track.clientWidth;
      this.track.scrollBy({ left: direction * step, behavior: this.behavior() });
    }

    update() {
      const trackBox = this.track.getBoundingClientRect();
      const visible = this.cards.map((card, index) => ({ box: card.getBoundingClientRect(), index })).filter(({ box }) => box.right > trackBox.left + 8 && box.left < trackBox.right - 8);
      this.previous.disabled = this.track.scrollLeft <= 2;
      this.next.disabled = this.track.scrollLeft >= this.track.scrollWidth - this.track.clientWidth - 2;
      const values = { first: visible.length ? visible[0].index + 1 : 0, last: visible.length ? visible[visible.length - 1].index + 1 : 0, total: this.cards.length };
      this.status.textContent = (this.dataset.countText || '[first]–[last] of [total] featured reviews').replace(/\[(\w+)\]/g, (token, key) => values[key] ?? token);
    }

    open(card) {
      this.querySelector('[data-dialog-author]').textContent = card.querySelector('[data-review-author]').textContent;
      this.querySelector('[data-dialog-body]').textContent = card.querySelector('[data-review-body]').textContent;
      const rating = this.querySelector('[data-dialog-rating]');
      const stars = Number(card.dataset.rating);
      rating.textContent = '★'.repeat(stars) + '☆'.repeat(5 - stars);
      rating.setAttribute('aria-label', `${stars} out of 5 stars`);
      const photo = card.querySelector('img');
      const dialogPhoto = this.querySelector('[data-dialog-image]');
      dialogPhoto.hidden = !photo;
      if (photo) { dialogPhoto.src = photo.currentSrc || photo.src; dialogPhoto.alt = photo.alt; }
      else { dialogPhoto.removeAttribute('src'); dialogPhoto.alt = ''; }
      this.dialog.showModal();
    }
  });
}
